/**
 * Furniture catalog for the cozy room.
 * Pieces are annotated SVG boxes (data-URI img src) with fixed aspect ratios and layout.
 *
 * Layout percentages are relative to a fixed-aspect room stage (ROOM_ASPECT), so the
 * composition scales uniformly with the viewport.
 */

import type {
  FurnitureGeometry,
  RoomFurnitureLayout,
  RoomFurnitureSlot,
} from "../lib/types.ts";

export const FurnitureKind = {
  Fireplace: "fireplace",
  Shelf: "shelf",
  Table: "table",
} as const;
export type FurnitureKind = (typeof FurnitureKind)[keyof typeof FurnitureKind];

/** Matches ROOM_BACKGROUND viewBox (width / height). */
export const ROOM_ASPECT = 1600 / 1000;

export interface FurnitureSpec {
  kind: RoomFurnitureSlot;
  /** SVG data-URI box with annotation */
  src: string;
  label: string;
  /** Width as percent of the room stage */
  widthPct: number;
  /** Width / height */
  aspectRatio: number;
  /** Horizontal anchor: left edge as % of room (center pieces use leftPct + transform) */
  leftPct: number;
  /** Bottom edge as % of room stage height */
  bottomPct: number;
  /** When true, leftPct is the center of the piece */
  centerX: boolean;
  zIndex: number;
}

/** Build an annotated rectangle as an SVG data-URI for use as img src. */
export function annotatedBoxSrc(
  label: string,
  aspectRatio: number,
  fill: string,
  stroke = "#2a2118",
): string {
  const width = 400;
  const height = Math.max(24, Math.round(width / aspectRatio));
  const fontSize = Math.max(11, Math.min(22, Math.round(Math.min(width, height) * 0.12)));
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    `<rect x="1.5" y="1.5" width="${width - 3}" height="${height - 3}" fill="${fill}" stroke="${stroke}" stroke-width="3" rx="2"/>`,
    `<text x="50%" y="50%" text-anchor="middle" dominant-baseline="central"`,
    ` font-family="ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"`,
    ` font-size="${fontSize}" font-weight="600" fill="${stroke}">${escapeXml(label)}</text>`,
    `</svg>`,
  ].join("");
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function escapeXml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

/** Room backdrop: wall + floor bands with annotation. */
export const ROOM_BACKGROUND = (() => {
  const w = 1600;
  const h = 1000;
  const floorY = 720;
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">`,
    `<rect width="${w}" height="${h}" fill="#d8d0c4"/>`,
    `<rect y="${floorY}" width="${w}" height="${h - floorY}" fill="#b9a894"/>`,
    `<line x1="0" y1="${floorY}" x2="${w}" y2="${floorY}" stroke="#2a2118" stroke-width="3"/>`,
    `<rect x="1.5" y="1.5" width="${w - 3}" height="${h - 3}" fill="none" stroke="#2a2118" stroke-width="3"/>`,
    `<text x="50%" y="18%" text-anchor="middle" dominant-baseline="central"`,
    ` font-family="ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"`,
    ` font-size="36" font-weight="600" fill="#2a2118">Room</text>`,
    `<text x="50%" y="88%" text-anchor="middle" dominant-baseline="central"`,
    ` font-family="ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"`,
    ` font-size="22" fill="#2a2118">floor</text>`,
    `</svg>`,
  ].join("");
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
})();

/** Piece height as % of room stage height (width% → px height → height%). */
export function furnitureHeightPct(spec: Pick<FurnitureSpec, "widthPct" | "aspectRatio">): number {
  return (spec.widthPct / spec.aspectRatio) * ROOM_ASPECT;
}

export function furnitureTopPct(spec: FurnitureSpec): number {
  return spec.bottomPct + furnitureHeightPct(spec);
}

/**
 * Layout is stacked intentionally:
 *   table on floor → fireplace behind → shelf on fireplace top → albums on shelf.
 */
const FIREPLACE: Omit<FurnitureSpec, "src"> = {
  kind: FurnitureKind.Fireplace,
  label: "Fireplace",
  widthPct: 36,
  aspectRatio: 1.15,
  leftPct: 50,
  bottomPct: 10,
  centerX: true,
  zIndex: 1,
};

const SHELF_WIDTH = 32;
const SHELF_ASPECT = 10;

export const FURNITURE: Record<FurnitureKind, FurnitureSpec> = {
  [FurnitureKind.Fireplace]: {
    ...FIREPLACE,
    src: annotatedBoxSrc("Fireplace", FIREPLACE.aspectRatio, "#c4a484"),
  },
  [FurnitureKind.Shelf]: {
    kind: FurnitureKind.Shelf,
    label: "Shelf",
    src: annotatedBoxSrc("Shelf", SHELF_ASPECT, "#8b6914"),
    widthPct: SHELF_WIDTH,
    aspectRatio: SHELF_ASPECT,
    leftPct: 50,
    bottomPct: FIREPLACE.bottomPct + furnitureHeightPct(FIREPLACE),
    centerX: true,
    zIndex: 3,
  },
  [FurnitureKind.Table]: {
    kind: FurnitureKind.Table,
    label: "Table",
    src: annotatedBoxSrc("Table", 2.4, "#a67c52"),
    widthPct: 26,
    aspectRatio: 2.4,
    leftPct: 50,
    bottomPct: 3,
    centerX: true,
    zIndex: 2,
  },
};

export const MIN_FURNITURE_WIDTH = 0.1;

export function furnitureGeometryHeight(
  geometry: Pick<FurnitureGeometry, "width">,
  aspectRatio: number,
): number {
  return (geometry.width * ROOM_ASPECT) / aspectRatio;
}

function defaultGeometry(spec: FurnitureSpec): FurnitureGeometry {
  const width = spec.widthPct / 100;
  const height = furnitureGeometryHeight({ width }, spec.aspectRatio);
  return {
    x: spec.centerX ? spec.leftPct / 100 - width / 2 : spec.leftPct / 100,
    y: 1 - spec.bottomPct / 100 - height,
    width,
  };
}

export const DEFAULT_ROOM_FURNITURE_LAYOUT: RoomFurnitureLayout = {
  shelf: defaultGeometry(FURNITURE[FurnitureKind.Shelf]),
  fireplace: defaultGeometry(FURNITURE[FurnitureKind.Fireplace]),
  table: defaultGeometry(FURNITURE[FurnitureKind.Table]),
};

function finiteOr(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

function shelfScale(width: number): number {
  return width / DEFAULT_ROOM_FURNITURE_LAYOUT.shelf.width;
}

/** Height above the shelf occupied by its album rail and spines. */
export function shelfRailHeight(width: number): number {
  return (ALBUM_SPINE.railHeightPct / 100) * shelfScale(width);
}

export function normalizeFurnitureGeometry(
  slot: RoomFurnitureSlot,
  geometry: Partial<FurnitureGeometry> | null | undefined,
): FurnitureGeometry {
  const fallback = DEFAULT_ROOM_FURNITURE_LAYOUT[slot];
  const spec = FURNITURE[slot as FurnitureKind];
  let width = Math.max(MIN_FURNITURE_WIDTH, finiteOr(geometry?.width ?? NaN, fallback.width));
  const maxWidthByHeight = spec.aspectRatio / ROOM_ASPECT;
  width = Math.min(width, 1, maxWidthByHeight);

  let x = finiteOr(geometry?.x ?? NaN, fallback.x);
  let y = finiteOr(geometry?.y ?? NaN, fallback.y);
  if (slot === FurnitureKind.Shelf) {
    const maxWidthByGroupHeight =
      1 /
      (ALBUM_SPINE.railHeightPct / 100 / DEFAULT_ROOM_FURNITURE_LAYOUT.shelf.width +
        ROOM_ASPECT / spec.aspectRatio);
    width = Math.min(width, maxWidthByGroupHeight);
    const pieceHeight = furnitureGeometryHeight({ width }, spec.aspectRatio);
    x = Math.min(Math.max(0, x), 1 - width);
    y = Math.min(Math.max(shelfRailHeight(width), y), 1 - pieceHeight);
    return { x, y, width };
  }

  const height = furnitureGeometryHeight({ width }, spec.aspectRatio);
  x = Math.min(Math.max(0, x), 1 - width);
  y = Math.min(Math.max(0, y), 1 - height);
  return { x, y, width };
}

export function normalizeRoomFurnitureLayout(
  layout: Partial<Record<RoomFurnitureSlot, Partial<FurnitureGeometry> | null>> | null | undefined,
): RoomFurnitureLayout {
  return {
    shelf: normalizeFurnitureGeometry("shelf", layout?.shelf),
    fireplace: normalizeFurnitureGeometry("fireplace", layout?.fireplace),
    table: normalizeFurnitureGeometry("table", layout?.table),
  };
}

export function moveFurnitureGeometry(
  slot: RoomFurnitureSlot,
  geometry: FurnitureGeometry,
  dx: number,
  dy: number,
): FurnitureGeometry {
  return normalizeFurnitureGeometry(slot, {
    ...geometry,
    x: geometry.x + dx,
    y: geometry.y + dy,
  });
}

function maxFurnitureWidthAt(slot: RoomFurnitureSlot, geometry: FurnitureGeometry): number {
  const spec = FURNITURE[slot as FurnitureKind];
  let maximum = Math.min(1 - geometry.x, ((1 - geometry.y) * spec.aspectRatio) / ROOM_ASPECT);
  if (slot === FurnitureKind.Shelf) {
    const railHeightPerWidth =
      ALBUM_SPINE.railHeightPct / 100 / DEFAULT_ROOM_FURNITURE_LAYOUT.shelf.width;
    maximum = Math.min(maximum, geometry.y / railHeightPerWidth);
  }
  return Math.max(MIN_FURNITURE_WIDTH, maximum);
}

export function resizeFurnitureGeometry(
  slot: RoomFurnitureSlot,
  geometry: FurnitureGeometry,
  pointerFromAnchorX: number,
  pointerFromAnchorY: number,
  stageWidth: number,
): FurnitureGeometry {
  const spec = FURNITURE[slot as FurnitureKind];
  const safeStageWidth = Math.max(1, stageWidth);
  const desiredWidthPx =
    (pointerFromAnchorX + pointerFromAnchorY / spec.aspectRatio) /
    (1 + 1 / spec.aspectRatio ** 2);
  const width = Math.min(
    Math.max(MIN_FURNITURE_WIDTH, desiredWidthPx / safeStageWidth),
    maxFurnitureWidthAt(slot, geometry),
  );
  return normalizeFurnitureGeometry(slot, { ...geometry, width });
}

export function resizeFurnitureGeometryBy(
  slot: RoomFurnitureSlot,
  geometry: FurnitureGeometry,
  deltaWidth: number,
): FurnitureGeometry {
  return normalizeFurnitureGeometry(slot, {
    ...geometry,
    width: Math.min(geometry.width + deltaWidth, maxFurnitureWidthAt(slot, geometry)),
  });
}

/**
 * Album spine sizes in room-stage container units (designed against 1600×1000).
 * Applied as cqw/cqh so spines scale with the stage, not as fixed px.
 */
export const ALBUM_SPINE = {
  widthCqw: (32 / 1600) * 100,
  heightCqh: (160 / 1000) * 100,
  gapCqw: (8 / 1600) * 100,
  /** Rail height as % of stage — slightly taller than spines */
  railHeightPct: (180 / 1000) * 100,
} as const;

export function applyFurnitureLayout(
  el: HTMLElement,
  spec: FurnitureSpec,
  geometry: FurnitureGeometry = DEFAULT_ROOM_FURNITURE_LAYOUT[spec.kind],
): void {
  const normalized = normalizeFurnitureGeometry(spec.kind, geometry);
  el.dataset.furniture = spec.kind;
  el.style.width = `${normalized.width * 100}%`;
  el.style.aspectRatio = String(spec.aspectRatio);
  el.style.left = `${normalized.x * 100}%`;
  el.style.top = `${normalized.y * 100}%`;
  el.style.bottom = "";
  el.style.zIndex = String(spec.zIndex);
  el.style.transform = "";
}
